<template>
  <div class="documentation-tab-content">
    <!-- Show existing documentation if it exists -->
    <div
      v-if="hasExistingDocumentation"
      class="documentation-tab-content__existing"
    />

    <!-- Show form interface -->
    <div class="documentation-tab-content__form-section">
      <div class="documentation-tab-content__header">
        <BaseButton
          :text="showForm ? 'Lukk skjema' : 'Last opp dokumentasjon'"
          variant="secondary"
          shape="rectangle"
          size="md"
          @click="toggleForm"
        />
      </div>

      <DocumentsForm
        v-if="showForm"
        :service-offer-id="serviceOfferId"
        :tender-id="tenderId"
        :supplier-request-id="supplierRequestId"
        :customer-org-number="customerOrgNumber"
        :supplier-id="supplierId"
        :existing-documentation="null"
        @hide="hideForm"
        @documentation-created="handleDocumentationCreated"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import DocumentsForm from "../DocumentsForm.vue";
import BaseButton from "@/components/Base/BaseButton/BaseButton.vue";
import { useDocumentationCache } from "@/composables/useDocumentationCache";
import type { ServiceOffer } from "@/custom-types/serviceOffers";
import type { DocumentationRecord } from "@/types/service-records";

interface Props {
  serviceOfferId: number;
  supplierRequestId: number;
  tenderId: number;
  customerOrgNumber: string;
  supplierId: number;
  acceptedServiceOffer?: ServiceOffer | null;
  localDocumentation?: DocumentationRecord | null;
}

interface Emits {
  (e: "documentation-created", documentation: DocumentationRecord): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Composables
const { getCachedDocumentation } = useDocumentationCache();

// Reactive state
const showForm = ref(false);

// Computed properties
const hasExistingDocumentation = computed(() => {
  // Check local documentation first (recently created in this session)
  if (props.localDocumentation) {
    return true;
  }

  // Check cached documentation (recently created and persisted)
  const cached = getCachedDocumentation(props.serviceOfferId);
  if (cached) {
    return true;
  }

  // Check if we have documentation from acceptedServiceOffer (new structure)
  const hasNewStructureDocs =
    !!props.acceptedServiceOffer?.supplierRequestAttachmentGroups?.length;

  // Check if we have documentation from acceptedServiceOffer (legacy structure)
  const hasLegacyDocs = !!(
    props.acceptedServiceOffer?.supplierRequestAttachments?.length ||
    props.acceptedServiceOffer?.supplierRequestAttachmentsDescription
  );

  return hasNewStructureDocs || hasLegacyDocs;
});

// Event handlers
const toggleForm = () => {
  showForm.value = !showForm.value;
};

const hideForm = () => {
  showForm.value = false;
};

const handleDocumentationCreated = (documentation: DocumentationRecord) => {
  showForm.value = false;
  emit("documentation-created", documentation);
};
</script>

<style lang="scss" scoped>
.documentation-tab-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  &__existing {
    width: 100%;
  }

  &__form-section {
    width: 100%;
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1rem;

    @media (max-width: $bp-sm) {
      gap: 0.75rem;
    }

    align-items: flex-start;

    @media (max-width: $bp-sm) {
      align-items: stretch;
    }
  }

  &__info {
    margin: 0;
    padding: 1rem;
    background-color: rgba($color-info, 0.1);
    color: $color-black;
    border-radius: 4px;
    font-size: 0.875rem;
    line-height: 1.5;
  }
}
</style>
